import IconAppsIntegrations from "@/icons/IconAppsIntegrations";
import IconAutomation from "@/icons/IconAutomation";
import IconBilling from "@/icons/IconBilling";
import IconChannels from "@/icons/IconChannels";
import IconGeneralSettings from "@/icons/IconGeneralSettings";
import IconHome from "@/icons/IconHome";
import IconWorkflows from "@/icons/IconWorkflows";
import { MenuItem } from "@/types";
import React from "react";
import { useMemo } from "react";
import useSubscriptions from "./useSubscriptions";
import IconKnowledgeBase from "@/icons/IconKnowledgeBase";
import IconDesigner from "@/icons/IconDesigner";
import IconReports from "@/icons/IconReports";

export default function useMenuItems() {
  const { subscription } = useSubscriptions();

  const menuItems: MenuItem[] = useMemo(() => {
    return [
      // {
      //   label: "Home",
      //   to: "/",
      //   icon: <IconHome />,
      //   enabled: true
      // },
      {
        label: "Settings",
        icon: <IconGeneralSettings />,
        sublinks: [
          {
            label: "Account",
            to: "/account",
            enabled: true
          },
          {
            label: "Users",
            to: "/users",
            enabled: true
          },
          {
            label: "Teams",
            to: "/teams",
            enabled: true
          },
          {
            label: "Skills",
            to: "/skills",
            enabled: true
          },
          {
            label: "Schedules",
            to: "/work_schedules",
            enabled: true
          },
          {
            label: "Surveys",
            to: "/surveys",
            enabled: true
          },
          {
            label: "Deployment",
            to: "/deployment",
            enabled: true
          },
          {
            label: "Invitations",
            to: "/invitations",
            enabled: subscription?.enableInvitations
          },
          {
            label: "Tags",
            to: "/tags",
            enabled: true
          },
          {
            label: "Custom Fields",
            to: "/custom-fields",
            enabled: true
          },
          {
            label: "Canned Responses",
            to: "/canned_responses",
            enabled: true
          },
          {
            label: "File Storage",
            to: "/files",
            enabled: true
          },
          {
            label: "Advanced Settings",
            to: "/settings",
            enabled: true
          }
        ],
        enabled: true
      },
      {
        label: "Designer",
        icon: <IconDesigner />,
        enabled: true,
        sublinks: [
          {
            label: "Chat Window",
            to: "/chat_window",
            enabled: true
          }
        ]
      },
      {
        label: "Workflows",
        icon: <IconWorkflows />,
        enabled: true,
        sublinks: [
          {
            label: "My Workflows",
            to: "/workflows",
            enabled: true
          },
          {
            label: "Variable Guide",
            to: "/workflows/variable-guide",
            enabled: true
          }
        ]
      },
      // {
      //   label: "Bots and Automation",
      //   to: "/bots",
      //   icon: <IconAutomation />,
      //   enabled: subscription?.enableAutomation
      // },
      // Hidden temporarily - set enabled to subscription?.enableAI to restore
      {
        label: "AI Chatbot",
        icon: <IconAutomation />,
        enabled: true, // was: subscription?.enableAI
        sublinks: [
          {
            label: "Chatbot Settings",
            to: "/ai",
            enabled: true
          },
          {
            label: "Chatbot Profile",
            to: "/bots",
            enabled: true
          },
          {
            label: "Chatbot Training",
            to: "/bots/training/index",
            enabled: true
          }
        ]
      },
      {
        label: "AI Chatbot (New)",
        icon: <IconAutomation />,
        enabled: true, // was: subscription?.enableAI,
        sublinks: [
          {
            label: "Chatbot Settings",
            to: "/ai-new/settings",
            enabled: true
          },
          {
            label: "Chatbot Training",
            to: "/ai-new/training",
            enabled: true
          },
          {
            label: "Knowledge Indexes",
            to: "/ai-new/indexes",
            enabled: true
          },
          {
            label: "Test Chat",
            to: "/ai-new/chat",
            enabled: true
          }
        ]
      },
      // Rules consolidated into Deployment page > Rules tab
      // Uncomment if a standalone nav item is needed again:
      // {
      //   label: "Rules",
      //   to: "/rules",
      //   icon: <IconWorkflows />,
      //   enabled: subscription?.enableWorkflowRules
      // },
      {
        label: "Reports",
        to: "/reports",
        icon: <IconReports />,
        enabled: true
      },
      {
        label: "Billing",
        to: "/billing",
        icon: <IconBilling />,
        enabled: true
      },
      {
        label: "Channels",
        to: "/channels",
        icon: <IconChannels />,
        sublinks: [
          {
            label: "Facebook",
            to: "/facebook",
            enabled: subscription?.enableFacebook
          },
          {
            label: "SMS",
            to: "/sms",
            enabled: subscription?.enableSms
          },
          {
            label: "WhatsApp",
            to: "/whatsapp",
            enabled: subscription?.enableWhatsapp
          },
          {
            label: "IVR",
            to: "/ivr",
            enabled: subscription?.enableIvr
          }
        ],
        enabled:
          subscription?.enableFacebook ||
          subscription?.enableSms ||
          subscription?.enableWhatsapp ||
          subscription?.enableIvr
      },
      {
        label: "Knowledge Base",
        to: "/kb",
        icon: <IconKnowledgeBase />,
        sublinks: [
          {
            label: "Settings",
            to: "/kb/settings",
            enabled: subscription?.enableKnowledgeBase
          },
          {
            label: "Topics",
            to: "/kb/topics",
            enabled: subscription?.enableKnowledgeBase
          },
          {
            label: "Articles",
            to: "/kb/articles",
            enabled: subscription?.enableKnowledgeBase
          },
          {
            label: "Site Map",
            to: "/kb/map",
            enabled: subscription?.enableKnowledgeBase
          }
        ],
        enabled: subscription?.enableKnowledgeBase
      },
      {
        label: "Integrations",
        to: "/integrations",
        icon: <IconAppsIntegrations />,
        sublinks: [
          {
            label: "Integrations",
            to: "/integrations",
            enabled: true
          },
          {
            label: "Log",
            to: "/integrations/log",
            enabled: true
          }
        ],
        enabled: true
      },
      // Admin Tools (Database Tool, Live Monitor) removed — redundant with messaging app
      // Routes still exist for direct URL access if needed
    ];
  }, [subscription]);

  return menuItems;
}
